import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'

/**
 * Locations that have an Account start date (`ghl_location_settings.new_system_start_at`, a day):
 * from that day the middleware does no new work for the location (only scheduled follow-ups run). The list page
 * greys these out and shows the date. Keyed by location id; locations without a date
 * are absent.
 */
export const NEW_SYSTEM_START_DATES_KEY = ['admin', 'ghl-new-system-start-dates'] as const

interface StartDateRow {
  readonly location_id: string
  readonly new_system_start_at: string | null
}

// Untyped cast: the generated database types predate the new_system_start_at column.
const settingsTable = supabase as unknown as {
  from: (t: string) => {
    select: (s: string) => {
      not: (column: string, operator: string, value: null) => Promise<{
        data: StartDateRow[] | null
        error: { message: string } | null
      }>
    }
  }
}

export function useNewSystemStartDates() {
  return useQuery<ReadonlyMap<string, string>>({
    queryKey: NEW_SYSTEM_START_DATES_KEY,
    queryFn: async () => {
      const { data, error } = await settingsTable
        .from('ghl_location_settings')
        .select('location_id, new_system_start_at')
        .not('new_system_start_at', 'is', null)
      if (error) throw new Error(error.message)
      return new Map(
        (data ?? [])
          .filter((row): row is StartDateRow & { new_system_start_at: string } => row.new_system_start_at !== null)
          .map((row) => [row.location_id, row.new_system_start_at])
      )
    },
    staleTime: 30 * 1000,
  })
}

/** "Nov 3, 2026" — the start is a calendar day; older timestamp values show their date. */
export function formatStartDate(raw: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(raw)
  if (!m) return raw
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])).toLocaleDateString(undefined, { dateStyle: 'medium' })
}
